import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, CATEGORIES, categoryColor, verdictColor } from "@/lib/pookieApi";
import { ArrowLeft, ArrowRight, Gavel, Loader2, Crown, Download, CheckSquare, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getDeviceId, getProEmail, useSubscription } from "@/lib/subscription";
import PaywallModal from "@/components/PaywallModal";

const STEPS = ["plaintiff_name", "defendant", "category", "tea", "demands"];

const stepConfig = {
  plaintiff_name: {
    title: "First, who are you, pookie?",
    sub: "Just a name or nickname for the record. No real legal names required (this isn't real court).",
    placeholder: "e.g. Heartbroken Hannah",
    type: "text",
    max: 80,
    bg: "bg-[#FFAEBC]",
  },
  defendant: {
    title: "Who's the defendant?",
    sub: "Name, initials, vibe, role — whoever did you dirty.",
    placeholder: "e.g. My situationship Brandon",
    type: "text",
    max: 120,
    bg: "bg-[#FFE600]",
  },
  category: {
    title: "What kind of crime is this?",
    sub: "Pick the courtroom we'll be unhinged in today.",
    placeholder: "",
    type: "category",
    max: 0,
    bg: "bg-[#B4F8C8]",
  },
  tea: {
    title: "Spill the tea.",
    sub: "Tell the court what happened. The juicier, the better.",
    placeholder: "On Tuesday, the defendant said 'we should hang' and then went MIA for 6 days…",
    type: "textarea",
    max: 2000,
    bg: "bg-[#A0E7E5]",
  },
  demands: {
    title: "Your demands? (optional)",
    sub: "What would justice look like in your perfect world?",
    placeholder: "A public apology and unlimited iced lattes for a month",
    type: "textarea",
    max: 500,
    bg: "bg-[#FFF5D6]",
  },
};

function Typewriter({ text, speed = 18, onDone }) {
  const [shown, setShown] = useState("");
  const idx = useRef(0);
  useEffect(() => {
    setShown("");
    idx.current = 0;
    if (!text) return;
    const interval = setInterval(() => {
      idx.current += 1;
      setShown(text.slice(0, idx.current));
      if (idx.current >= text.length) {
        clearInterval(interval);
        onDone && onDone();
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, onDone]);
  return <span className="pl-caret">{shown}</span>;
}

export default function FilePage() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    plaintiff_name: "",
    defendant: "",
    category: "",
    tea: "",
    demands: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const sub = useSubscription();

  const total = STEPS.length;
  const key = STEPS[step];
  const cfg = stepConfig[key];
  const progress = Math.round(((step + 1) / total) * 100);

  const valid = useMemo(() => {
    if (key === "demands") return true;
    if (key === "category") return !!form.category;
    if (key === "tea") return form.tea.trim().length >= 10;
    return (form[key] || "").trim().length > 0;
  }, [form, key]);

  const next = () => {
    if (!valid) {
      toast("The court requires more information, pookie.");
      return;
    }
    if (step < total - 1) setStep(step + 1);
    else submit();
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/cases", {
        ...form,
        device_id: getDeviceId(),
        email: getProEmail(),
      });
      setResult(data);
      sub.refresh && sub.refresh();
      setTimeout(() => setShowStamp(true), 1200); // after typewriter starts
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.code === "QUOTA_EXCEEDED") {
        setPaywallOpen(true);
      } else {
        const msg = typeof detail === "string" ? detail : "The court is overwhelmed. Try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Verdict reveal screen -----
  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10" data-testid="verdict-screen">
        <div className="pl-chip mb-5 bg-[#FFE600]">★ Verdict delivered</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter">
          The court has reached a decision.
        </h1>

        <div className="mt-8 pl-card bg-white p-6 md:p-10">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="pl-chip font-mono-pl">{result.case_number}</span>
            <span className={`pl-chip ${categoryColor(result.category)}`}>
              {CATEGORIES.find((c) => c.key === result.category)?.label || result.category}
            </span>
            <span className="pl-chip">Drama: {result.drama_score}/10</span>
          </div>

          <div className="text-center py-8">
            <div className="pl-label">Final Verdict</div>
            <div className="mt-3">
              {showStamp ? (
                <span
                  className={`pl-stamp pl-stamp-animate text-3xl md:text-5xl ${verdictColor(result.verdict)}`}
                  data-testid="verdict-stamp"
                >
                  {result.verdict}
                </span>
              ) : (
                <div className="font-mono-pl text-sm text-black/60 inline-flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} /> Judge Pookie is stamping…
                </div>
              )}
            </div>
          </div>

          <div className="pl-card-flat bg-[#FFF5D6] p-5 md:p-6">
            <div className="pl-label mb-3">Judgment</div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="verdict-judgment">
              <Typewriter text={result.judgment} speed={26} />
            </h2>
          </div>

          <div className="pl-card-flat bg-white p-5 md:p-6 mt-5 border-dashed">
            <div className="pl-label mb-3">Court Reasoning</div>
            <p className="font-mono-pl text-sm md:text-base whitespace-pre-wrap leading-relaxed">
              <Typewriter text={result.reasoning} speed={10} />
            </p>
          </div>

          <div className="pl-card-flat bg-[#B4F8C8] p-5 md:p-6 mt-5">
            <div className="pl-label mb-3">Your Sentence</div>
            <p className="font-mono-pl text-base md:text-lg" data-testid="verdict-sentence">
              {result.sentence}
            </p>
          </div>

          {result.tasks && result.tasks.length > 0 && (
            <div className="pl-card-flat bg-[#FFAEBC] p-5 md:p-6 mt-5" data-testid="verdict-tasks">
              <div className="pl-label mb-3 flex items-center gap-2">
                <CheckSquare size={14} strokeWidth={3} /> Court-Ordered Tasks
              </div>
              <ul className="space-y-3">
                {result.tasks.map((t, idx) => (
                  <li key={idx} className="flex items-start gap-3 font-mono-pl text-sm md:text-base" data-testid={`verdict-task-${idx}`}>
                    <span className="mt-1 w-5 h-5 flex-none rounded-md border-[3px] border-black bg-white" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <p className="font-mono-pl text-xs mt-4 text-black/60">
                Download the PDF and actually do them. Court adjourned only after every box is checked.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => nav(`/case/${result.id}`)}
            className="pl-btn pl-btn-primary"
            data-testid="verdict-view-case"
          >
            View case file <ArrowRight size={16} strokeWidth={3} />
          </button>
          <button
            onClick={() => {
              window.open(`${API}/cases/${result.id}/pdf`, "_blank", "noopener,noreferrer");
              toast.success("Downloading your official ruling.");
            }}
            className="pl-btn pl-btn-secondary"
            data-testid="verdict-download-pdf"
          >
            <Download size={16} strokeWidth={3} /> Download PDF
          </button>
          <button
            onClick={async () => {
              const pdfUrl = `${API}/cases/${result.id}/pdf`;
              // Try Web Share API with a file first
              if (navigator.canShare && window.fetch) {
                try {
                  const resp = await fetch(pdfUrl);
                  const blob = await resp.blob();
                  const file = new File(
                    [blob],
                    `Pookielawyer-${result.case_number || "ruling"}.pdf`,
                    { type: "application/pdf" },
                  );
                  if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                      title: `Pookielawyer verdict — ${result.case_number}`,
                      text: result.judgment,
                      files: [file],
                    });
                    return;
                  }
                } catch (err) {
                  if (err?.name === "AbortError") return;
                }
              }
              // Fallback: copy PDF link to clipboard
              try {
                await navigator.clipboard.writeText(pdfUrl);
                toast.success("PDF link copied — paste it anywhere, pookie.");
              } catch {
                window.open(pdfUrl, "_blank", "noopener,noreferrer");
              }
            }}
            className="pl-btn pl-btn-mint"
            data-testid="verdict-share-pdf"
          >
            <Share2 size={16} strokeWidth={3} /> Share PDF
          </button>
          <button
            onClick={() => {
              setResult(null);
              setShowStamp(false);
              setStep(0);
              setForm({ plaintiff_name: "", defendant: "", category: "", tea: "", demands: "" });
            }}
            className="pl-btn pl-btn-ghost"
            data-testid="verdict-file-another"
          >
            File another case
          </button>
          <button onClick={() => nav("/archive")} className="pl-btn pl-btn-ghost">
            Browse archive
          </button>
        </div>
      </div>
    );
  }

  // ----- Wizard -----
  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-10" data-testid="file-wizard">
      {/* Quota indicator */}
      <div className="mb-5 flex items-center justify-between flex-wrap gap-2" data-testid="quota-indicator">
        {sub.is_pro ? (
          <span className="pl-chip bg-[#B4F8C8]">
            <Crown size={12} strokeWidth={3} /> Pookie Pro · unlimited verdicts
          </span>
        ) : sub.loading ? (
          <span className="pl-chip">Loading quota…</span>
        ) : (
          <>
            <span className="pl-chip bg-white">
              {Math.max(0, sub.free_remaining)} / {sub.free_quota} free verdicts left
            </span>
            <button
              onClick={() => setPaywallOpen(true)}
              className="pl-chip cursor-pointer bg-[#FFE600] font-display"
              data-testid="quota-upgrade"
            >
              <Crown size={12} strokeWidth={3} /> Go Pro · ₹99
            </button>
          </>
        )}
      </div>

      {/* progress */}
      <div className="flex items-center gap-3 mb-6">
        <div className="pl-label">Step {step + 1} of {total}</div>
        <div className="h-3 flex-1 border-[3px] border-black rounded-full overflow-hidden bg-white">
          <div
            className="h-full bg-[#FF3B30] transition-all"
            style={{ width: `${progress}%` }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      <div className={`pl-card p-6 md:p-10 ${cfg.bg}`}>
        <h1 className="font-display text-3xl md:text-5xl tracking-tighter leading-tight" data-testid="step-title">
          {cfg.title}
        </h1>
        <p className="font-mono-pl text-sm md:text-base text-black/75 mt-3">{cfg.sub}</p>

        <div className="mt-6">
          {cfg.type === "text" && (
            <input
              data-testid={`input-${key}`}
              autoFocus
              className="pl-input text-lg"
              placeholder={cfg.placeholder}
              maxLength={cfg.max}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && next()}
            />
          )}
          {cfg.type === "textarea" && (
            <>
              <textarea
                data-testid={`input-${key}`}
                autoFocus
                className="pl-textarea text-base"
                placeholder={cfg.placeholder}
                maxLength={cfg.max}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
              <div className="text-right font-mono-pl text-xs mt-1 text-black/60">
                {(form[key] || "").length}/{cfg.max}
              </div>
            </>
          )}
          {cfg.type === "category" && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="category-grid">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`category-${c.key}`}
                  onClick={() => setForm({ ...form, category: c.key })}
                  className={`pl-card p-4 text-left ${c.color} ${
                    form.category === c.key ? "ring-4 ring-black" : ""
                  }`}
                >
                  <div className="font-display text-lg uppercase">{c.label}</div>
                  <div className="font-mono-pl text-xs text-black/70 mt-1">
                    {c.key === "relationship" && "Situationships, ghosting, mixed signals"}
                    {c.key === "friendship" && "Group chat betrayals, plan flakers"}
                    {c.key === "daily-life" && "Roommate crimes, queue cuts, small chaos"}
                    {c.key === "family" && "Mom guilt, sibling theft, cousin shade"}
                    {c.key === "workplace" && "Slack passive aggression, credit theft"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-8">
        <button
          onClick={back}
          disabled={step === 0 || submitting}
          className="pl-btn pl-btn-ghost"
          data-testid="wizard-back"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </button>
        <button
          onClick={next}
          disabled={!valid || submitting}
          className={`pl-btn ${step === total - 1 ? "pl-btn-primary" : "pl-btn-secondary"}`}
          data-testid="wizard-next"
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Judge Pookie is deliberating…
            </>
          ) : step === total - 1 ? (
            <>
              <Gavel size={16} strokeWidth={3} /> Deliver Verdict
            </>
          ) : (
            <>
              Next <ArrowRight size={16} strokeWidth={3} />
            </>
          )}
        </button>
      </div>

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        freeUsed={sub.free_used}
      />
    </div>
  );
}
